import { createFileRoute, Link } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { ARTICLES, articlePath } from "../lib/content";
import { QUIZZES, quizMinutes, quizPath } from "../lib/quiz";
import {
  breadcrumbJsonLd,
  canonical,
  collectionJsonLd,
  documentHeaders,
  socialMeta,
} from "../lib/seo";

const TITLE = "Christian quizzes and tests — Bible Shield";
const DESCRIPTION =
  "Bible quizzes and honest self-tests: which character you are, what your prayer style is, and where your phone is taking your attention. No sign-up, nothing stored.";

/**
 * `/quiz` — the hub.
 *
 * ── Why quizzes get their own section rather than living in the blog ─────────
 *
 * They answer a different kind of search. An article answers a question
 * somebody can type; a quiz answers the ones they cannot — "how bad is mine",
 * "which one am I", "is this normal" — and those are searched in volumes that
 * dwarf the advice queries. Putting them under `/blog` would bury the pattern
 * under a heading that says "writing", and a hub is worth more than a category:
 * hubs get crawled more often, which is most of why anything new gets found.
 *
 * The cross-links at the bottom are the point of having both. A quiz-taker is
 * two clicks from the argument behind the result, and a reader is one click
 * from the thing they will actually forward.
 */
export const Route = createFileRoute("/quiz/")({
  headers: documentHeaders,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...socialMeta({
        title: TITLE,
        description: DESCRIPTION,
        path: "/quiz",
        lang: "en",
      }),
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Bible Shield", path: "/" },
          { name: "Quizzes", path: "/quiz" },
        ]),
      },
      {
        "script:ld+json": collectionJsonLd({
          name: TITLE,
          description: DESCRIPTION,
          path: "/quiz",
          items: QUIZZES.map((q) => ({ name: q.h1, path: quizPath(q.slug) })),
        }),
      },
    ],
    links: [{ rel: "canonical", href: canonical("/quiz") }],
  }),
  component: QuizIndex,
});

function QuizIndex() {
  return (
    <PageChrome>
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        quizzes
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        Tests that are not flattery
      </h1>
      <p className="mt-4 max-w-[60ch] text-lg leading-relaxed text-ink2">
        Four of them. Every question and every result is on the page before you
        start, nothing is stored, and none of them ends by telling you that you
        are wonderful.
      </p>

      <ul className="mt-10 grid gap-5 sm:grid-cols-2">
        {QUIZZES.map((quiz) => (
          <li key={quiz.slug}>
            <Link
              to="/quiz/$slug"
              params={{ slug: quiz.slug }}
              className="brutal flex h-full flex-col gap-3 p-6 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
                {quiz.questions.length} questions · {quizMinutes(quiz)} min
              </span>
              <span className="font-display text-xl leading-snug font-bold text-ink">
                {quiz.h1}
              </span>
              <span className="leading-relaxed text-body">
                {quiz.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <section className="mt-14 border-t-[2.5px] border-ink pt-10">
        <h2 className="font-display text-2xl font-bold text-ink">
          The writing behind them
        </h2>
        <p className="mt-3 max-w-[60ch] leading-relaxed text-ink2">
          Every result above points at an argument made properly somewhere else.
          These are the three most people go to next.
        </p>
        <ul className="mt-5 grid gap-4 sm:grid-cols-3">
          {ARTICLES.slice(0, 3).map((article) => (
            <li key={article.slug}>
              <a
                href={articlePath(article.slug)}
                className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
              >
                <span className="font-display text-lg leading-snug font-bold text-ink">
                  {article.h1}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </section>
    </PageChrome>
  );
}
